import { useEffect, useRef, useState } from 'react'

import { useGameStore } from '../store/gameStore'

const COUNTS = [10, 25, 50, 100] as const

/**
 * Autospin count picker.
 *
 * A native <select> was the wrong instrument. It was drawn exactly like the
 * Bet dropdown, so the one control that commits the player to a hundred
 * consecutive bets read as just another setting — and its resting label was
 * an em dash, which names neither the feature nor its state.
 *
 * A chip plus a popover of counts costs one extra click and buys two things:
 * the control no longer impersonates Bet, and starting a run becomes a
 * deliberate act rather than a stray scroll over a focused select.
 */
export function AutoSpinMenu({ disabled }: { disabled: boolean }): React.JSX.Element {
  const autoSpinsLeft = useGameStore((s) => s.autoSpinsLeft)
  const startAutoSpins = useGameStore((s) => s.startAutoSpins)
  const [open, setOpen] = useState(false)
  const rootRef = useRef<HTMLDivElement>(null)

  const running = autoSpinsLeft > 0

  // A popover that outlives a click elsewhere is a trap on a bar this dense.
  useEffect(() => {
    if (!open) return
    const onDown = (e: PointerEvent): void => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false)
    }
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('pointerdown', onDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('pointerdown', onDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  // Once a run starts there is nothing left to pick: the spin button is the
  // stop button, and a menu hanging open over it would only mislead.
  useEffect(() => {
    if (running || disabled) setOpen(false)
  }, [running, disabled])

  return (
    <div className="auto" ref={rootRef}>
      <button
        className={`mini-toggle auto__chip ${running ? 'mini-toggle--on' : ''}`}
        type="button"
        disabled={disabled || running}
        aria-haspopup="menu"
        aria-expanded={open}
        title="Autospin: run a fixed number of spins"
        onClick={() => setOpen((v) => !v)}
      >
        {running ? autoSpinsLeft : 'OFF'}
      </button>

      {open && (
        <div className="auto__menu" role="menu" aria-label="Autospin count">
          {COUNTS.map((n) => (
            <button
              key={n}
              className="auto__count"
              role="menuitem"
              type="button"
              onClick={() => {
                setOpen(false)
                startAutoSpins(n)
              }}
            >
              {n}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
